import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import useTickets from "../../../hooks/useTickets";
import { PAGE_STATE } from "../../../constants";
import { format } from "date-fns";
import Button from "../../common/Button";
import useUsers from "../../../hooks/useUsers";
import AppContext from "../../../AppContext";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import leftArrowBlue from "../../../assets/icons/arrow-left-blue.png";
import { ReactComponent as SendSvg } from "../../../assets/icons/send.svg";
import { ReactComponent as AttachmentSvg } from "../../../assets/icons/chat/attachment.svg";
import { ReactComponent as TrashSvg } from "../../../assets/icons/trash.svg";

const SupportTag = () => (
  <div className="ticket__support-tag">Urbaan support</div>
);

const View = ({ id }) => {
  const { user } = useContext(AppContext);
  const [ticket, setTicket] = useState(undefined);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [attachments, setAttachments] = useState([]);
  const { getStaff } = useUsers();
  const navigate = useNavigate();
  const {
    getTicket,
    queryTicketComments,
    bookTicket,
    addComment,
    closeTicket,
    uploadTicketCommentAttachments,
    patchTicketComment,
  } = useTickets();
  const inputRef = useRef();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    let _ticket = await getTicket(id);
    _ticket.createdBy = user;

    // * Default unbooked
    _ticket.booked = false;

    // * Get staff if booked
    if (_ticket.staff) {
      const staff = await getStaff(_ticket.staff);
      _ticket.support = staff;
      _ticket.booked = true;
    }

    // * Get comments
    let { data: _comments } = await queryTicketComments(id, {}, null, -1);
    _ticket.comments = _comments;

    setTicket(_ticket);
    setPageState(PAGE_STATE.IDLE);
  };

  const handleReturnClick = () => {
    navigate(-1);
  };

  const handleCommentSubmit = async ({ msg }, { resetForm }) => {
    const _newComment = await addComment(
      ticket._id,
      msg,
      attachments.length === 0
    );

    if (!_newComment.active && attachments.length > 0) {
      _newComment.attachments = await uploadTicketCommentAttachments(
        ticket._id,
        _newComment._id,
        attachments
      );

      // * Patch ticket back to active
      await patchTicketComment(ticket._id, _newComment._id, { active: true });
    }

    // * Add comment to state
    setTicket((curr) => ({
      ...curr,
      comments: [...curr.comments, _newComment],
    }));
    setAttachments([]);
    resetForm();
  };

  if (pageState === PAGE_STATE.ERROR) {
    return <div className="ticket-view">Error</div>;
  }

  if (pageState === PAGE_STATE.LOADING) {
    return <div className="ticket-view">Loading</div>;
  }

  const {
    _id,
    topic,
    desc,
    comments,
    createdBy,
    createdOn,
    booked,
    support,
    closed,
    closedOn,
    no,
  } = ticket;

  return (
    <div className="ticket">
      <div className="privacy-return" onClick={handleReturnClick}>
        <img alt="return" src={leftArrowBlue} width={24} />
        Return to Your Tickets
      </div>
      <div className="ticket-header">
        <img className="ticket-header__avatar" src={createdBy.avatar} />
        <div className="ticket-header-title">
          <div className="ticket-header-title__id">
            #{(no ?? 1).toString().padStart(6, "0")}
          </div>
        </div>
        <div className="ticket-header__topic">{topic}</div>
        <div className="ticket-header-info">
          <span className="ticket-header-info__label">Opened by</span>
          <span className="ticket-header-info__label ticket-header-info__label--link">
            You
          </span>
          <span className="ticket-header-info__label">
            on {format(new Date(createdOn), "MMM dd, yyyy")}
            {support ? ", " : ""}
          </span>
          {support && (
            <>
              <span className="ticket-header-info__label">Answered by</span>
              <span className="ticket-header-info__label ticket-header-info__label--link">
                {support.firstname} {support.lastname || ""}
              </span>
              <SupportTag />
            </>
          )}
        </div>
      </div>
      <div className="ticket-desc">{desc}</div>
      {booked && comments && (
        <div className="ticket-comments">
          <div className="ticket-comments__count">
            {comments.length} Comments
          </div>
          {comments.map((c) => {
            const _isSupport = c.createdBy === support._id;

            return (
              <div className="ticket-comments-comment" key={c._id}>
                <img
                  className="ticket-comments-comment__owner"
                  src={_isSupport ? support.avatar : createdBy.avatar}
                />
                <div className="ticket-comments-comment__arrow"></div>
                <div className="ticket-comments-comment-info">
                  <div className="ticket-comments-comment-info-header">
                    <span className="ticket-comments-comment-info-header__name">
                      {_isSupport
                        ? `${support.firstname} ${support.lastname || ""}`
                        : `${createdBy.firstname} ${createdBy.surname || ""}`}
                    </span>
                    <span className="ticket-comments-comment-info-header__date">
                      on {format(new Date(c.createdOn), "MMM dd, yyyy")}
                    </span>
                    {_isSupport && <SupportTag />}
                  </div>
                  <p className="ticket-comments-comment-info-value">
                    {c.comment}
                  </p>
                </div>
              </div>
            );
          })}
          {!closed && (
            <div
              className="chat-input"
              style={{
                padding: "0",
                marginTop: 24,
              }}
            >
              <div className="chat-input-actions">
                <div className="chat-input-actions-action"></div>
              </div>
              <Formik
                initialValues={{ msg: "" }}
                onSubmit={handleCommentSubmit}
              >
                {({ values, setFieldValue }) => {
                  const handleInputChanged = ({ target: { value } }) => {
                    setFieldValue("msg", value);
                  };

                  return (
                    <Form className="chat-input-textbox">
                      <input
                        ref={inputRef}
                        className="chat-input-textbox__input"
                        placeholder="Type a message"
                        value={values.msg}
                        onChange={handleInputChanged}
                      />
                      <SendSvg className="chat-input-textbox__send-button" />
                    </Form>
                  );
                }}
              </Formik>
            </div>
          )}
        </div>
      )}
      {closed && (
        <span className="ticket__closed-info">
          This ticket has been closed on{" "}
          {format(new Date(closedOn), "MMM dd, yyyy HH:mm")}
        </span>
      )}
    </div>
  );
};

View.propTypes = {};

export default View;
